import React, { useContext, useMemo, useState } from "react";

import { useLocation } from "@reach/router";
import classNames from "classnames";

import LogoBlock from "@/components/common/logoBlock";
import GlobalContext from "@/contexts/Global/GlobalContext";
import UserContext from "@/contexts/User/UserContext";
import { isPathContains } from "@/utils/urlUtils";
import { Icon } from "@components/common/icon";
import Support from "@components/common/support";

import FooterColumn from "./Column";
import { FOOTER_DATA, PROMO_CODES_LINK } from "./constants";
import FooterMenu from "./Menu";
import PaymentInfo from "./PaymentInfo";

import "./styles.scss";

const {
  menus: { knowledge, paymentInfo, aboutUs, benefits },
  beExpert,
  socialLinks,
  bottomData,
} = FOOTER_DATA;

const CommonFooter = () => {
  // Путь из `useLocation` одинаковый на сервере и в браузере, `window.location` на сервере нет
  const { pathname } = useLocation();
  const { isUserLoggedIn } = useContext(UserContext);
  const { isExpert } = useContext(GlobalContext);
  const [isFullTextShown, setIsFullTextShown] = useState(false);

  const showMoreClickHandler = () =>
    setIsFullTextShown((oldValue) =>
      !oldValue);

  // Промокоды — раздел личного кабинета клиента: гостям, поисковикам и экспертам ссылку не показываем
  const convertedBenefits = useMemo(() =>
    ({
      title: benefits.title,
      items: benefits.items.filter((item) =>
        item.link !== PROMO_CODES_LINK || (isUserLoggedIn && !isExpert)),
    }), [isUserLoggedIn, isExpert]);

  return (
    <footer className="footer">
      <div className="page-width">
        <div className="footer__inner">
          <div className="footer__left">
            <div className="footer__column footer__logo">
              <LogoBlock className="footer__logo-link" isFooter />
              <div className="footer__label">{FOOTER_DATA.logoSubtitle}</div>
            </div>
            <div className="footer__column footer__social">
              {socialLinks.map((social) =>
                (
                  <a
                    className={`footer__social-item footer__social-item--${social.title.toLowerCase()}`}
                    title={social.title}
                    href={social.link}
                    key={social.title}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon type={social.title} />
                  </a>
                ))}
            </div>
            <FooterColumn className="footer__support">
              <Support />
            </FooterColumn>
          </div>
          <div className="footer__right">
            <div className="footer__right-column">
              <FooterColumn>
                <>
                  <h3 className="h4">{aboutUs.title}</h3>
                  <FooterMenu itemsList={aboutUs.items} />
                  <h3 className="h4">{convertedBenefits.title}</h3>
                  <FooterMenu itemsList={convertedBenefits.items} />
                  <h3 className="h4">{paymentInfo.title}</h3>
                  <PaymentInfo />
                </>
              </FooterColumn>
            </div>
            <div className="footer__right-column">
              <FooterColumn title={knowledge.title}>
                <FooterMenu itemsList={knowledge.items} />
              </FooterColumn>
              <FooterColumn title={beExpert.title}>
                <>
                  <a title={beExpert.anchor.title} href={beExpert.anchor.link}>
                    {beExpert.anchor.title}
                  </a>
                  <div className="footer__label">{beExpert.description}</div>
                  <div className="footer__details">
                    <a href={beExpert.details.link}>
                      {beExpert.details.title}
                    </a>
                  </div>
                </>
              </FooterColumn>
            </div>
          </div>
          <div
            className={classNames("footer__bottom", {
              "footer__margin-bottom": isPathContains("/expert/", pathname),
            })}
          >
            <noindex>
              <div className="footer__policies">
                <p>
                  {`Сервис Лунаро (lunaro.ru) предназначен для\u00a0лиц старше 18\u00a0лет и\u00a0используется в\u00a0информационно-развлекательных целях.`}
                </p>
                <p>
                  Использование Сервиса Лунаро регулируется
                  {" "}
                  <a
                    title={bottomData.agreement.title}
                    href={bottomData.agreement.link}
                    rel="noreferrer"
                  >
                    {bottomData.agreement.name}
                  </a>
                  {", "}
                  <a
                    title={bottomData.confidence.title}
                    href={bottomData.confidence.link}
                    rel="noreferrer"
                  >
                    {bottomData.confidence.name}
                  </a>
                  {" и\u00a0"}
                  <a
                    title={bottomData.cookie.title}
                    href={bottomData.cookie.link}
                    rel="noreferrer"
                  >
                    {bottomData.cookie.name}
                  </a>
                  {`. Порядок подбора эксперта и\u00a0проведения консультаций определяется`}
                  {" "}
                  <a
                    title={bottomData.agency.title}
                    href={bottomData.agency.link}
                    rel="noreferrer"
                  >
                    {bottomData.agency.name}
                  </a>
                  {", "}
                  <a
                    title={bottomData.referral.title}
                    href={bottomData.referral.link}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {bottomData.referral.name}
                  </a>
                  {" и\u00a0"}
                  <a
                    title={bottomData.serviceRules.title}
                    href={bottomData.serviceRules.link}
                    rel="noreferrer"
                  >
                    {bottomData.serviceRules.name}
                  </a>
                  , принимаемыми пользователем отдельно перед началом консультации.
                </p>
                <p
                  className={isFullTextShown ? "" : "mobile-hidden"}
                >
                  {`Эксперты являются независимыми исполнителями и\u00a0не\u00a0относятся к\u00a0работникам или\u00a0представителям Сервиса Лунаро. Сервис Лунаро проводит проверку экспертов, однако не\u00a0гарантирует достоверность прогнозов, наступление предполагаемых событий или\u00a0достижение пользователем ожидаемого результата.`}
                </p>
                <p
                  className={isFullTextShown ? "" : "mobile-hidden"}
                >
                  {`Консультации не\u00a0являются медицинской или\u00a0психотерапевтической помощью. Эксперты не\u00a0ставят диагнозы, не\u00a0назначают лечение и\u00a0лекарственные препараты. По\u00a0вопросам здоровья необходимо обращаться к\u00a0квалифицированному специалисту или\u00a0в\u00a0медицинскую организацию.`}
                </p>
                <p
                  className={isFullTextShown ? "" : "mobile-hidden"}
                >
                  {`ООО\u00a0«Футура» 199004, г.\u00a0Санкт-Петербург, 8-я линия В.О., д.\u00a025, лит.\u00a0А, помещ.\u00a015-Н, офис\u00a014-1 ИНН\u00a07801716423 КПП\u00a0780101001 ОГРН\u00a01227800119007`}
                </p>
                <div
                  className="footer__show-more"
                  onClick={showMoreClickHandler}
                  onKeyPress={showMoreClickHandler}
                  role="button"
                  tabIndex={0}
                >
                  {isFullTextShown ? "Скрыть" : "Показать ещё"}
                </div>
              </div>
              <div className="footer__copyright">{bottomData.copyright}</div>
            </noindex>
            {/* вне <noindex>: ссылку должны видеть поисковики и AI-краулеры */}
            <div className="footer__llms">
              <a
                title={bottomData.llms.title}
                href={bottomData.llms.link}
                type="text/plain"
              >
                {bottomData.llms.name}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default CommonFooter;
